import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getEntities, getPicturesData, exportPicturesData, createPicturesShare, getHashtagsForManufacturer, SERVER_API } from "../../API";
import { ColumnFilter, PlainHeader } from "../../components/ColumnFilter";
import { Icon } from "../../util/Icon";
import DropdownMultiSelect from "../../components/DropdownMultiSelect";

function normalizeList(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function formatDate(epoch) {
  if (!epoch) return "";
  return new Date(epoch).toLocaleDateString("en-CA");
}

const PRIORITY_LABELS = { 0: "Low", 1: "Medium", 2: "High", 3: "Critical" };
function priorityLabel(level) { return PRIORITY_LABELS[level] ?? String(level); }

const DISPLAY_LABELS = { 0: "", 1: "Primary", 2: "Secondary" };
function displayLabel(value) { return DISPLAY_LABELS[value] ?? String(value); }

function uniqueValues(arr) {
  return Array.from(new Set(arr.filter((v) => v !== null && v !== undefined && v !== ""))).sort();
}

function matches(selected, value) {
  return selected.length === 0 || selected.includes(value);
}


export default function PicturesReport() {
  const [{ manufacturer, fromDate, toDate, provinces, territories, representatives }] =
    useContext(ReportParamsContext);

  const [banners, setBanners] = useState([]);
  const [selectedBanners, setSelectedBanners] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [selectedHashtags, setSelectedHashtags] = useState([]);
  const [allPhotos, setAllPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const [filterDate, setFilterDate] = useState([]);
  const [filterTerritory, setFilterTerritory] = useState([]);
  const [filterBanner, setFilterBanner] = useState([]);
  const [filterPriority, setFilterPriority] = useState([]);
  const [filterDisplay, setFilterDisplay] = useState([]);
  const [filterHashtag, setFilterHashtag] = useState([]);
  const [visibleCount, setVisibleCount] = useState(200);

  useEffect(() => {
    getEntities("banners", "?size=1000").then((r) => setBanners(normalizeList(r).sort((a, b) => a.name.localeCompare(b.name))));
  }, []);

  useEffect(() => {
    if (!manufacturer) { setHashtags([]); setSelectedHashtags([]); return; }
    getHashtagsForManufacturer(manufacturer.id)
      .then((list) => setHashtags(list.map((h) => h.hashtag)))
      .catch(() => setHashtags([]));
    setSelectedHashtags([]);
  }, [manufacturer]);

  const handleLoad = async () => {
    if (!manufacturer) {
      setError("Please select a manufacturer in the parameters panel.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getPicturesData({
        manufacturerId: manufacturer.id,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        bannerIds: selectedBanners.map((b) => b.id),
        provinceIds: provinces.map((p) => p.id),
        territoryIds: territories.map((t) => t.id),
        representativeIds: representatives.map((r) => r.id),
        hashtags: selectedHashtags,
      });
      setAllPhotos(data);
      setLoaded(true);
      setVisibleCount(200);
      setFilterDate([]);
      setFilterTerritory([]);
      setFilterBanner([]);
      setFilterPriority([]);
      setFilterDisplay([]);
      setFilterHashtag([]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportPicturesData({
        manufacturerId: manufacturer.id,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        bannerIds: selectedBanners.map((b) => b.id),
        provinceIds: provinces.map((p) => p.id),
        territoryIds: territories.map((t) => t.id),
        representativeIds: representatives.map((r) => r.id),
        hashtags: selectedHashtags,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const { token } = await createPicturesShare({
        manufacturerId: manufacturer.id,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        bannerIds: selectedBanners.map((b) => b.id),
        provinceIds: provinces.map((p) => p.id),
        territoryIds: territories.map((t) => t.id),
        representativeIds: representatives.map((r) => r.id),
        hashtags: selectedHashtags,
      });
      setShareUrl(`${window.location.origin}/shared/photos/${token}`);
      setCopied(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  };

  const dateOptions      = useMemo(() => uniqueValues(allPhotos.map((p) => formatDate(p.date))), [allPhotos]);
  const territoryOptions = useMemo(() => uniqueValues(allPhotos.map((p) => p.territoryName)), [allPhotos]);
  const bannerOptions    = useMemo(() => uniqueValues(allPhotos.map((p) => p.bannerName)), [allPhotos]);
  const priorityOptions  = useMemo(() => uniqueValues(allPhotos.map((p) => priorityLabel(p.priorityLevel))), [allPhotos]);
  const displayOptions   = useMemo(() => uniqueValues(allPhotos.map((p) => displayLabel(p.display))), [allPhotos]);
  const hashtagOptions   = useMemo(() => uniqueValues(allPhotos.map((p) => p.hashtag)), [allPhotos]);

  const photos = useMemo(() => {
    setVisibleCount(200);
    return allPhotos.filter((p) => {
      if (!matches(filterDate,      formatDate(p.date)))             return false;
      if (!matches(filterTerritory, p.territoryName))                return false;
      if (!matches(filterBanner,    p.bannerName))                   return false;
      if (!matches(filterPriority,  priorityLabel(p.priorityLevel))) return false;
      if (!matches(filterDisplay,   displayLabel(p.display)))        return false;
      if (!matches(filterHashtag,   p.hashtag))                      return false;
      return true;
    });
  }, [allPhotos, filterDate, filterTerritory, filterBanner, filterPriority, filterDisplay, filterHashtag]);

  useEffect(() => {
    if (expandedIndex === null) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") setExpandedIndex((i) => (i + 1) % photos.length);
      else if (e.key === "ArrowLeft") setExpandedIndex((i) => (i - 1 + photos.length) % photos.length);
      else if (e.key === "Escape") setExpandedIndex(null);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [expandedIndex, photos.length]);

  return (
    <div className="flex-grow-1 overflow-auto">
      <div className="container-fluid py-4 px-4">

        <div className="card shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label fw-medium mb-1">Banners</label>
                <DropdownMultiSelect
                  options={banners}
                  value={selectedBanners}
                  onChange={setSelectedBanners}
                  getOptionLabel={(b) => b.name}
                  getOptionValue={(b) => b.id}
                  placeholder="All banners"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-medium mb-1">Hashtags</label>
                <DropdownMultiSelect
                  options={hashtags}
                  value={selectedHashtags}
                  onChange={setSelectedHashtags}
                  getOptionLabel={(h) => h}
                  getOptionValue={(h) => h}
                  placeholder="All hashtags"
                  disabled={hashtags.length === 0}
                />
              </div>
              <div className="col-md-auto">
                <button className="btn btn-primary" onClick={handleLoad} disabled={loading}>
                  {loading ? "Loading…" : "Load Pictures"}
                </button>
              </div>
              {loaded && (
                <>
                  <div className="col-md-auto">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleExport}
                      disabled={exporting}
                      title="Export to Excel"
                    >
                      <Icon iconName="FileEarmarkExcel" className="me-1" />
                      {exporting ? "Exporting…" : "Export"}
                    </button>
                  </div>
                  <div className="col-md-auto">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleShare}
                      disabled={sharing}
                      title="Generate shareable link"
                    >
                      <Icon iconName="Share" className="me-1" />
                      {sharing ? "Generating…" : "Share"}
                    </button>
                  </div>
                </>
              )}
            </div>
            {shareUrl && (
              <div className="row mt-3">
                <div className="col">
                  <div className="input-group">
                    <input type="text" className="form-control form-control-sm" value={shareUrl} readOnly />
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleCopy}>
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {loaded && (
          <div className="text-muted small mb-2">
            {photos.length} photo{photos.length !== 1 ? "s" : ""}
            {photos.length > visibleCount && ` — showing first ${visibleCount}`}
          </div>
        )}

        {loaded && (
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: "0.9rem" }}>
              <thead className="table-light">
                <tr>
                  <ColumnFilter label="Date"           options={dateOptions}      value={filterDate}      onChange={setFilterDate} />
                  <ColumnFilter label="Territory"      options={territoryOptions} value={filterTerritory} onChange={setFilterTerritory} />
                  <ColumnFilter label="Chain"          options={bannerOptions}    value={filterBanner}    onChange={setFilterBanner} />
                  <PlainHeader  label="Store" />
                  <PlainHeader  label="Note"  minWidth={200} />
                  <ColumnFilter label="Priority Level" options={priorityOptions}  value={filterPriority}  onChange={setFilterPriority} />
                  <ColumnFilter label="Display"        options={displayOptions}   value={filterDisplay}   onChange={setFilterDisplay} />
                  <ColumnFilter label="Hashtag"        options={hashtagOptions}   value={filterHashtag}   onChange={setFilterHashtag} />
                  <PlainHeader  label="Photo" />
                </tr>
              </thead>
              <tbody>
                {photos.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-muted py-4">
                      No pictures match the current filters.
                    </td>
                  </tr>
                )}
                {photos.slice(0, visibleCount).map((p, i) => (
                  <tr key={i}>
                    <td>{formatDate(p.date)}</td>
                    <td>{p.territoryName}</td>
                    <td>{p.bannerName}</td>
                    <td>{p.storeName}{p.storeNo ? ` ${p.storeNo}` : ""}{p.storeCity ? ` - ${p.storeCity}` : ""}</td>
                    <td>{p.note}</td>
                    <td>{priorityLabel(p.priorityLevel)}</td>
                    <td>{displayLabel(p.display)}</td>
                    <td>{p.hashtag}</td>
                    <td style={{ width: 90 }}>
                      <img
                        src={`${SERVER_API}${p.fileUrl}`}
                        alt=""
                        loading="lazy"
                        style={{ width: 80, height: 60, objectFit: "cover", cursor: "pointer", borderRadius: 3 }}
                        onClick={() => setExpandedIndex(i)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loaded && photos.length > visibleCount && (
          <div className="text-center my-3">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setVisibleCount((n) => n + 200)}
            >
              Show more ({photos.length - visibleCount} remaining)
            </button>
          </div>
        )}

        {expandedIndex !== null && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 9999 }}
            onClick={() => setExpandedIndex(null)}
          >
            <img
              src={`${SERVER_API}${photos[expandedIndex].fileUrl}`}
              alt=""
              style={{ maxHeight: "90vh", maxWidth: "80vw", objectFit: "contain", borderRadius: 6 }}
            />
            {photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setExpandedIndex((expandedIndex - 1 + photos.length) % photos.length); }}
                  style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 44, height: 44, color: "#fff", fontSize: 22, cursor: "pointer" }}
                >‹</button>
                <button
                  onClick={(e) => { e.stopPropagation(); setExpandedIndex((expandedIndex + 1) % photos.length); }}
                  style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 44, height: 44, color: "#fff", fontSize: 22, cursor: "pointer" }}
                >›</button>
                <div style={{ position: "absolute", bottom: 16, color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                  {expandedIndex + 1} / {photos.length}
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
